/** @jsxImportSource @emotion/react */
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  Divider,
} from '@mui/material'
import { Story, Branding } from '@app/components/Risk/types'
import {
  formSectionStyles,
  formLabelStyles,
  formHelperTextStyles,
  colorPickerStyles,
} from './StoryBuilder.styles'
import { defaultBranding } from '../Stories.utils'

interface StoryMetadataFormProps {
  story: Partial<Story>
  onUpdate: (updates: Partial<Story>) => void
}

interface ColorPickerProps {
  label: string
  value: string
  onChange: (value: string) => void
}

const ColorPicker = ({ label, value, onChange }: ColorPickerProps) => {
  const theme = useTheme()

  return (
    <Box css={colorPickerStyles(theme)}>
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="color-swatch"
        style={{ backgroundColor: value }}
      />
      <TextField
        size="small"
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="color-input"
      />
    </Box>
  )
}

export const StoryMetadataForm = ({ story, onUpdate }: StoryMetadataFormProps) => {
  const theme = useTheme()
  const branding = story.branding || defaultBranding

  const handleBrandingUpdate = (field: keyof Branding, value: string) => {
    onUpdate({
      branding: {
        ...branding,
        [field]: value,
      },
    })
  }

  return (
    <Box>
      <Box css={formSectionStyles(theme)}>
        <Box sx={{ fontWeight: 600, fontSize: '1.1rem', mb: 4 }}>Story Details</Box>

        <Box sx={{ mb: 4 }}>
          <Box css={formLabelStyles(theme)}>Title *</Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter story title"
            value={story.title || ''}
            onChange={(e) => onUpdate({ title: e.target.value })}
            error={!story.title}
            helperText={!story.title ? 'Title is required' : ''}
          />
        </Box>

        <Box sx={{ mb: 4 }}>
          <Box css={formLabelStyles(theme)}>Description *</Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            placeholder="Brief description of the story"
            value={story.description || ''}
            onChange={(e) => onUpdate({ description: e.target.value })}
            error={!story.description}
            helperText={!story.description ? 'Description is required' : ''}
          />
        </Box>

        <Box sx={{ mb: 4 }}>
          <Box css={formLabelStyles(theme)}>Summary</Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            size="small"
            placeholder="Extended summary for the story landing page (optional)"
            value={story.summary || ''}
            onChange={(e) => onUpdate({ summary: e.target.value })}
          />
          <Box css={formHelperTextStyles(theme)}>
            This appears on the story landing page before users start the guided tour
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Box css={formLabelStyles(theme)}>Cover Image URL</Box>
          <TextField
            fullWidth
            size="small"
            placeholder="https://example.com/cover.jpg"
            value={story.coverImageUrl || ''}
            onChange={(e) => onUpdate({ coverImageUrl: e.target.value })}
          />
          <Box css={formHelperTextStyles(theme)}>Save image in S3 and use CloudFront url</Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Box css={formLabelStyles(theme)}>Visibility</Box>
          <FormControl fullWidth size="small">
            <Select
              value={story.visibility || 'Private'}
              onChange={(e) => onUpdate({ visibility: e.target.value })}
            >
              <MenuItem value="Private">Private</MenuItem>
              <MenuItem value="Public">Public</MenuItem>
            </Select>
          </FormControl>
          <Box css={formHelperTextStyles(theme)}>
            Public stories can be shared via link without login
          </Box>
        </Box>
        <Box sx={{ mb: 4 }}>
          <Box css={formLabelStyles(theme)}>Default Basemap</Box>
          <FormControl fullWidth size="small">
            <Select
              value={story.basemap || 'Street'}
              onChange={(e) => onUpdate({ basemap: e.target.value })}
            >
              <MenuItem value="Street">Street</MenuItem>
              <MenuItem value="Satellite">Satellite</MenuItem>
              <MenuItem value="Contours">Contours</MenuItem>
            </Select>
          </FormControl>
          <Box css={formHelperTextStyles(theme)}>
            Used when a step doesn't specify its own basemap
          </Box>
        </Box>
      </Box>

      <Box css={formSectionStyles(theme)}>
        <Box sx={{ fontWeight: 600, fontSize: '1.1rem', mb: 4 }}>Branding Colours</Box>
        <Box css={formHelperTextStyles(theme)} sx={{ mb: 4 }}>
          Customize the story appearance with your brand colours
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <ColorPicker
            label="Primary Colour"
            value={branding.primary}
            onChange={(value) => handleBrandingUpdate('primary', value)}
          />
          <ColorPicker
            label="Primary Contrast"
            value={branding.primaryContrast}
            onChange={(value) => handleBrandingUpdate('primaryContrast', value)}
          />
          <ColorPicker
            label="Secondary Colour"
            value={branding.secondary}
            onChange={(value) => handleBrandingUpdate('secondary', value)}
          />
          <ColorPicker
            label="Secondary Contrast"
            value={branding.secondaryContrast}
            onChange={(value) => handleBrandingUpdate('secondaryContrast', value)}
          />
        </Box>

        <Box sx={{ mt: 2, p: 2, borderRadius: 1, bgcolor: branding.primary }}>
          <Box sx={{ color: branding.primaryContrast, fontWeight: 500 }}>Primary Color Preview</Box>
        </Box>
        <Box sx={{ mt: 1, p: 2, borderRadius: 1, bgcolor: branding.secondary }}>
          <Box sx={{ color: branding.secondaryContrast, fontWeight: 500 }}>
            Secondary Color Preview
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default StoryMetadataForm
